<fw-dialog width="small" [showClose]="false">
  <fw-dialog-header>
    <h3>Create Reader Task</h3>
    <!-- Used custom btn to handle close with confirmation -->
    <fw-icon-button
      tabindex="-1" icon="close" color="slate"
      (click)="closeDialog()">
    </fw-icon-button>
  </fw-dialog-header>
  <fw-dialog-content [formGroup]="createTaskForm">

    <fw-layout-panel border="all" formGroupName="selectTaskLocation" [rounded]="true">
      <fw-form-heading title="Task Linking"></fw-form-heading>

      @if (!dialogData?.projectId) {
        <fw-wrapped-input title="Project">
          @if (projects$ | async; as projects) {
            <fw-select formControlName="project"
              iconProperty="icon"
              maxOptionsHeight="300px"
              placeholder="Select Project"
              staticIcon="folder-open"
              titleProperty="label"
              valueProperty="_id"
              width="100%"
              [descriptionProperty]="undefined"
              [options]="projects"
              [showFilter]="true"
              [useFullOptionAsValue]="true"
            ></fw-select>
          } @else {
            <fw-select placeholder="Select Project"
              staticIcon="folder-open"
              width="100%"
              [options]="[]"
              [disabled]="true"
            ></fw-select>
          }
        </fw-wrapped-input>
      }

      @if ({data: protocols$ | async}; as protocols) {
        @if (protocols.data && !protocols.data.length && createTaskForm.get('selectTaskLocation.project').valid) {
          <fw-alert
            icon="information-circle"
            severity="warning"
            title="No Read Protocols"
            variant="standard"
            [description]=" 'No read protocols found for project labeled ' + createTaskForm.get('selectTaskLocation.project')?.value?.label + '. See documentation for details to add a protocol for read tasks.'"
          ></fw-alert>
        }

        <fw-wrapped-input title="Read Protocol">
          <fw-select formControlName="readProtocol"
            maxOptionsHeight="300px"
            placeholder="Select Read Protocol"
            titleProperty="label"
            valueProperty="_id"
            width="100%"
            [options]="protocols.data"
          ></fw-select>
        </fw-wrapped-input>
      }

      @if (!dialogData?.containerType) {
        <fw-layout-panel border="all" formGroupName="container" [rounded]="true">
          <fw-wrapped-input title="Container Type">
            <fw-select formControlName="type"
              maxOptionsHeight="300px"
              placeholder="Select Container Type"
              width="100%"
            >
              @for (ct of containerTypes; track ct.value) {
                <fw-menu-item [title]="containerTypeLabel[ct.value] || ''" [value]="ct.value"></fw-menu-item>
              }
            </fw-select>
          </fw-wrapped-input>

          <footer class="container-type-footer">
            @if ({data: containerTags$ | async}; as containerTags) {
              <app-expansion-panel
                icon="filter"
                title="Container Filters"
                [expanded]="false"
              >
                <div>
                  <fw-wrapped-input title="Includes Tags"></fw-wrapped-input>
                  @if (containerTags.data) {
                    <fw-typeahead formControlName="includesTags" [options]="containerTags.data" [allowNew]="false"></fw-typeahead>
                  } @else {
                    <fw-typeahead [disabled]="true"></fw-typeahead>
                  }
                </div>
                <div>
                  <fw-wrapped-input title="Excludes Tags"></fw-wrapped-input>
                  @if (containerTags.data) {
                    <fw-typeahead formControlName="excludeTags" [options]="containerTags.data" [allowNew]="false"></fw-typeahead>
                  } @else {
                    <fw-typeahead [disabled]="true"></fw-typeahead>
                  }
                </div>
              </app-expansion-panel>
            }
          </footer>
        </fw-layout-panel>
      }

      @if ({data: containersCount$ | async}; as count) {
        @if (!isLoadingContainers() && count.data !== null && count.data !== undefined && !dialogData?.containerId) {
          <fw-alert
            icon="information-circle"
            severity="info"
            variant="standard"
            [description]="count.data === 1
              ? '1 reader task will be created based on the filter settings.'
              : 'You will make ' + count.data + ' reader tasks. To make a single task, find the \'Create Task\' option directly on the desired container\'s page in the application.'"
            [title]="count.data > 1
              ? count.data + ' of ' + createTaskForm.get('selectTaskLocation.container.type').value + '(-s) Found'
              : ''"
          ></fw-alert>
        }
      }
    </fw-layout-panel>

    <fw-layout-panel border="all" formGroupName="taskDetail" [rounded]="true">
      <fw-form-heading title="Task Detail"></fw-form-heading>

      <fw-wrapped-input
        title="Task Description"
        [helperText]="(createTaskForm.get('taskDetail.description')?.value?.length || 0) + ' of 250 characters'"
      >
        <fw-textarea-input formControlName="description"
          placeholder="Enter a task description"
          width="100%"
          [maxRows]="8"
          [minRows]="3"
        ></fw-textarea-input>
      </fw-wrapped-input>

      <fw-layout-group [padded]="false">
        <fw-wrapped-input title="Due Date">
          <fw-date-input formControlName="dueDate"
            placeholder="Select Due Date"
            type="date"
          ></fw-date-input>
        </fw-wrapped-input>

        <fw-wrapped-input title="Priority" data-class="priority-input">
          <fw-select formControlName="priority"
            width="100%"
            [style.--task-selected-priority-color]="priorityCSSVarIconColor()"
          >
            @for (priority of priorities; track priority.value) {
              <fw-menu-item [iconColor]="prioritiesIconColors[priority.value]"
                [icon]="prioritiesIcons[priority.value]"
                [title]="taskPrioritiesLabels[priority.value]"
                [value]="priority.value"
              ></fw-menu-item>
            }
          </fw-select>
        </fw-wrapped-input>
      </fw-layout-group>

      <div>
        <fw-wrapped-input title="Task Tags"></fw-wrapped-input>
        <fw-typeahead formControlName="taskTags" [options]="taskTags"></fw-typeahead>
      </div>
    </fw-layout-panel>

    <fw-layout-panel border="all" formGroupName="assignTask" [rounded]="true">
      <fw-form-heading title="Assign Task"></fw-form-heading>

      <ng-container *ngVar="staffingPools$ | async as staffingPools">
        <ng-container *ngVar="filteredUsers$ | async as users">
          <fw-wrapped-input>
            @if (users && staffingPools) {
              <fw-select maxOptionsHeight="40vh"
                placeholder="Select user or staffing pool..."
                width="100%"
                (change)="onAssigneeChanged($event, staffingPools)"
              >
                @for (pool of staffingPools; track pool._id) {
                  <fw-menu-item [description]="pool.description"
                    [title]="pool.label"
                    [value]="pool._id"
                  >
                    <fw-avatar color="secondary"
                      content="icon"
                      icon="user-profile-cards-pool"
                      size="medium"
                      variant="circular"
                    ></fw-avatar>
                  </fw-menu-item>
                }
                <fw-menu-separator></fw-menu-separator>
                @for (user of users; track user._id) {
                  <fw-menu-item [description]="user.email"
                    [disabled]="user.disabled"
                    [title]="user.firstname + ' ' + user.lastname"
                    [value]="user._id"
                  >
                    <fw-avatar size="medium"
                      variant="circular"
                      [appUser]="user">
                    </fw-avatar>
                  </fw-menu-item>
                }
              </fw-select>
            } @else {
            <!-- fw-select can't correct handle ng-content changes, that's hack to "redraw" list with static content -->
            <fw-select width="100%"
              placeholder="Select user or staffing pool..."
              [disabled]="true"
            ></fw-select>
          }
          </fw-wrapped-input>
        </ng-container>
      </ng-container>
    </fw-layout-panel>

  </fw-dialog-content>
  <fw-dialog-actions>
    <fw-button variant="outline" [disabled]="isCreating()" (click)="closeDialog()">
      Cancel
    </fw-button>
    <fw-button
      [disabled]="createTaskForm.invalid || isCreating() || createTaskForm.get('selectTaskLocation.readProtocol').disabled || !(containersCount$ | async)"
      [variant]="isCreating() ? 'progress' : 'solid'"
      (click)="createTask()"
    >
      Create
    </fw-button>
  </fw-dialog-actions>
</fw-dialog>
